import { Card } from "@/components/ui/card";
import { formatNumberWithCommas } from "@/utils/formatNumberWithCommas";
import { GroupUserIcon, ThumbaCoin } from "@roman/shared/components/Icons";
import type { PredictionMarketWithProbability } from "@roman/shared/types/PredictionMarket";
import { getPredictionMarketType, getYesNoChoices } from "@roman/shared/utils/predictionMarket/utils";
import { useNavigate } from "react-router-dom";
import { EntryShareButton } from "../PredictionChart/EntryShareButton";
import { Tag } from "../Tag";
import { Title } from "./Title";

interface Props {
  market: PredictionMarketWithProbability & {
    totalEnteredFreePoints: number;
    numberOfParticipants: number;
  };
  choiceComponent: React.ReactNode;
}

export const MarketCardListItem = ({ market, choiceComponent }: Props) => {
  const navigate = useNavigate();
  const { id: marketId, title } = market;
  const predictionMarketType = getPredictionMarketType(market.predictionMarketChoices.map((c) => ({ name: c.name })));

  return (
    <Card
      className="hover:shadow-lg transition-shadow duration-200 cursor-pointer flex flex-col"
      onClick={() => navigate(`/prediction-market/details/${marketId}`)}
    >
      {market.primaryImageURL && (
        <div className="w-full h-[103px]">
          <img src={market.primaryImageURL} alt={title} className="w-full object-cover rounded-t-lg  h-[103px]" />
        </div>
      )}
      <div className="p-4 flex flex-col items-stretch">
        <div>
          <div className="flex justify-between items-center">
            {market.label && (
              <div>
                <Tag text={market.label} className="text-xs px-[10px] py-1.5 leading-3" />
              </div>
            )}

            <div className="flex flex-col gap-1 text-xs text-gray-600 text-light-gray">
              <div className="flex flex-1 justify-between items-center gap-4" onClick={(e) => e.stopPropagation()}>
                <EntryShareButton market={market} className="text-dark-gray w-5 h-5" />
                {/* <BookmarkIcon className="text-dark-gray w-5 h-5" /> */}
              </div>
            </div>
          </div>
          <Title
            text={title}
            className="pb-0 mb-3 text-[17px] leading-5 line-clamp-2 overflow-hidden font-bold min-h-[2.5rem]"
          />
        </div>

        <div className="flex-shrink">
          {predictionMarketType === "YESNO" &&
            (() => {
              const { yesChoice, noChoice } = getYesNoChoices(market.predictionMarketChoices);
              const yesPercent = Math.round(yesChoice.probability * 100);
              const noPercent = Math.round(noChoice.probability * 100);
              return (
                <div className="flex items-center justify-between mb-3 px-2">
                  <div className="flex items-center gap-1">
                    <svg width="6" height="6" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="5" cy="5" r="5" fill="#00B398" />
                    </svg>
                    <span className="font-bold text-gray">YES</span>
                    <span className="font-bold text-black">{yesPercent}%</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <svg width="6" height="6" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="5" cy="5" r="5" fill="#FF5A37" />
                    </svg>
                    <span className="font-bold text-gray">NO</span>
                    <span className="font-bold text-black">{noPercent}%</span>
                  </div>
                </div>
              );
            })()}
          <div onClick={(e) => e.stopPropagation()}>{choiceComponent}</div>
        </div>

        <div className="flex items-center justify-between mt-3">
          <div className="flex items-center gap-2">
            <ThumbaCoin className="w-6 h-6" />
            <div className="text-lg font-bold">{formatNumberWithCommas(market.totalEnteredFreePoints)} pt</div>
          </div>
          <div className="flex items-center gap-[7px] text-dark-gray text-xs mr-0.5">
            <div className="flex items-center gap-[2.82px]">
              <GroupUserIcon />
              <span>参加数</span>
            </div>
            <span className="font-bold text-sm">{formatNumberWithCommas(market.numberOfParticipants)}</span>
          </div>
        </div>
      </div>
    </Card>
  );
};
